import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API } from "@/lib/api";
import { Newspaper, Clock, ArrowRight, Sparkles } from "lucide-react";

const CATEGORY_ACCENT = {
  "cee-technique": "#10B981",
  "renovation": "#0055A4",
  "environnement": "#0EA5E9",
  "reglementation": "#8B5CF6",
};

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
}

export default function BlogHighlight() {
  const [article, setArticle] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/blog/highlight`).then((r) => (r.ok ? r.json() : null)),
      fetch(`${API}/blog/stats`).then((r) => (r.ok ? r.json() : null)),
    ])
      .then(([hl, st]) => {
        setArticle(hl);
        setStats(st);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !article) return null;

  const accent = CATEGORY_ACCENT[article.category] || "#10B981";

  return (
    <section data-testid="blog-highlight" className="bg-white">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-24 md:py-32">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-10 w-10 rounded-xl bg-[#064E3B] grid place-items-center text-[#10B981]">
                <Newspaper className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <div className="text-[11px] font-semibold tracking-[0.28em] text-[#10B981] uppercase">
                Actualité de la semaine
              </div>
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] tracking-tight leading-[1.05]">
              Actualités ECONATION.
            </h2>
            <p className="mt-4 text-[#475569] md:text-lg leading-relaxed">
              Décryptages CEE, rénovation & environnement — nouvelles actualités publiées chaque semaine par nos experts.
            </p>
          </div>

          {stats && (
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="text-right">
                <div className="font-display text-4xl md:text-5xl font-bold text-[#0F172A] leading-none">
                  {stats.published_this_year}
                </div>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#94A3B8] mt-2">
                  Actualités en {stats.year}
                </div>
              </div>
              <div className="h-14 w-[1px] bg-black/10" />
              <Link
                to="/blog"
                data-testid="highlight-see-all"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#0F172A] hover:bg-[#064E3B] text-white text-sm font-semibold transition-colors group"
              >
                Voir toutes les actualités
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          )}
        </div>

        {/* Featured article — split layout */}
        <Link
          to={`/blog/${article.slug}`}
          data-testid="highlight-article"
          className="group grid md:grid-cols-2 gap-0 bg-white rounded-3xl overflow-hidden border border-black/[0.06] hover:border-[#10B981]/40 hover:shadow-[0_40px_100px_-40px_rgba(15,23,42,0.35)] transition-all duration-500"
        >
          {/* Image */}
          <div className="relative aspect-[4/3] md:aspect-auto overflow-hidden bg-[#F1F5F9]">
            <img
              src={article.image_url}
              alt={article.title}
              className="h-full w-full object-cover group-hover:scale-[1.05] transition-transform duration-[1.5s]"
              loading="lazy"
            />
            <div className="absolute top-5 left-5">
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-semibold tracking-[0.14em] uppercase text-white backdrop-blur-md"
                style={{ backgroundColor: `${accent}E6` }}
              >
                <Sparkles className="h-3 w-3" />
                {article.category_label}
              </span>
            </div>
          </div>

          {/* Content */}
          <div className="p-8 md:p-10 lg:p-14 flex flex-col justify-center">
            <div className="text-[11px] font-semibold tracking-[0.24em] uppercase text-[#94A3B8] mb-4">
              Publié le {formatDate(article.created_at)}
            </div>
            <h3 className="font-display text-2xl md:text-3xl lg:text-[34px] font-bold text-[#0F172A] tracking-tight leading-[1.1] group-hover:text-[#064E3B] transition-colors">
              {article.title}
            </h3>
            <p className="mt-5 text-[#475569] leading-relaxed md:text-lg line-clamp-3">
              {article.excerpt}
            </p>
            <div className="mt-8 flex items-center justify-between">
              <div className="inline-flex items-center gap-1.5 text-[13px] text-[#64748B]">
                <Clock className="h-3.5 w-3.5" />
                {article.reading_time} min de lecture
              </div>
              <div className="inline-flex items-center gap-2 text-[14px] font-semibold text-[#10B981] group-hover:gap-3 transition-all">
                Lire l&apos;article
                <ArrowRight className="h-4 w-4" />
              </div>
            </div>
          </div>
        </Link>
      </div>
    </section>
  );
}
